import { useEffect, useRef } from 'react';

/**
 * Keep data fresh in long-lived PWA tabs without asking the user to reload.
 *
 * Fires `refresh()` on:
 *   1) `visibilitychange` when the document becomes visible again
 *   2) Window `focus`
 *   3) Global `app:refresh` custom event (fired by <RefreshButton />)
 *   4) Interval polling — DISABLED by default. Pass `intervalMs > 0` to opt in
 *      (e.g. NotificationBell uses 30 s for near-real-time alerts).
 *
 * Callers pass a callback and can opt-out via `enabled: false`.
 */
export function useAutoRefresh(refresh, { intervalMs = 0, enabled = true } = {}) {
    const savedRefresh = useRef(refresh);
    const lastRunAt = useRef(0);
    const lastHiddenAt = useRef(0);
    useEffect(() => {
        savedRefresh.current = refresh;
    }, [refresh]);

    useEffect(() => {
        if (!enabled) return undefined;

        // Skip auto-refresh if the tab was only briefly hidden (< 30 s) —
        // that's typically a native file picker / camera / system share
        // that steals focus, NOT a user coming back after a long absence.
        // Manual clicks on the 🔄 button and `app:refresh` always fire.
        const MIN_HIDDEN_MS = 30000;
        // Also throttle: at most one auto refresh per 30 s.
        const MIN_INTERVAL_MS = 30000;

        const run = (force = false) => {
            const now = Date.now();
            if (!force && now - lastRunAt.current < MIN_INTERVAL_MS) return;
            lastRunAt.current = now;
            const fn = savedRefresh.current;
            if (!fn) return;
            try {
                const maybePromise = fn();
                if (maybePromise && typeof maybePromise.catch === 'function') {
                    maybePromise.catch(() => { /* swallow */ });
                }
            } catch { /* ignore */ }
        };

        let intervalId = null;
        if (intervalMs > 0) {
            intervalId = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                    run();
                }
            }, intervalMs);
        }

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                lastHiddenAt.current = Date.now();
                return;
            }
            // Only refresh if tab was hidden for a meaningful time (>30 s).
            const hiddenFor = Date.now() - (lastHiddenAt.current || 0);
            if (lastHiddenAt.current && hiddenFor >= MIN_HIDDEN_MS) run();
        };
        const onFocus = () => {
            // Focus alone (window regains focus) is unreliable on mobile
            // where file pickers trigger it. Only refresh if tab was
            // actually hidden long enough.
            const hiddenFor = Date.now() - (lastHiddenAt.current || 0);
            if (lastHiddenAt.current && hiddenFor >= MIN_HIDDEN_MS) run();
        };
        const onManual = () => run(true);  // Manual button — bypass throttle

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        window.addEventListener('app:refresh', onManual);

        return () => {
            if (intervalId !== null) clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('app:refresh', onManual);
        };
    }, [enabled, intervalMs]);
}

/** Manually trigger a global refresh (used by header button). */
export function triggerAppRefresh() {
    try {
        window.dispatchEvent(new CustomEvent('app:refresh'));
    } catch { /* ignore */ }
}
